// Iconografía de Busco sobre **Lucide** (la librería que usan Linear, Vercel, shadcn):
// trazo consistente, grilla de 24 y miles de iconos mantenidos.
// Este wrapper fija el grosor/tamaño por defecto y da nombres semánticos en
// español-neutro para que las pantallas no dependan del nombre exacto de la librería.
//
// Regla del proyecto: NADA de emojis en la interfaz — se ven distinto en cada
// sistema, no se pueden colorear ni alinear con el texto.
import {
  AlertTriangle, ArrowRight, BadgeCheck, Battery, Bell, Building2, Camera,
  Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, CircleDot,
  Clock, Cpu, CreditCard, Eye, FileText, Gamepad2, Handshake, Home, Image as ImageIcon,
  Info, Laptop, LayoutList, LogOut, Mail, MapPin, MessageCircle, MessageSquare, Minus,
  Network, Package, PackageCheck, Pencil, Phone, Plus, Search, Send,
  Settings, Share2, Shield, ShieldCheck, ShoppingCart, Smartphone, Sparkles,
  Star, Store, Tag, Trash2, TrendingUp, Truck, Tv, Upload, User, Users,
  Wrench, X, XCircle, Zap, LucideProps,
} from "lucide-react";

const MAP = {
  search: Search, wrench: Wrench, store: Store, user: User, users: Users,
  phone: Phone, mail: Mail, shield: Shield, "shield-check": ShieldCheck,
  "badge-check": BadgeCheck, star: Star, "map-pin": MapPin, clock: Clock,
  chat: MessageCircle, send: Send, bell: Bell, camera: Camera, image: ImageIcon,
  upload: Upload, file: FileText, check: Check, "check-circle": CheckCircle2,
  x: X, "x-circle": XCircle, alert: AlertTriangle, info: Info,
  "chevron-left": ChevronLeft, "chevron-right": ChevronRight, "chevron-down": ChevronDown,
  plus: Plus, minus: Minus, cart: ShoppingCart, package: Package,
  "package-check": PackageCheck, truck: Truck, tag: Tag, chart: TrendingUp,
  list: LayoutList, settings: Settings, logout: LogOut, smartphone: Smartphone,
  laptop: Laptop, tv: Tv, gamepad: Gamepad2, battery: Battery, cpu: Cpu,
  sparkles: Sparkles, "trending-up": TrendingUp, network: Network,
  "arrow-right": ArrowRight, eye: Eye, trash: Trash2, edit: Pencil,
  dot: CircleDot, zap: Zap, home: Home, building: Building2,
  card: CreditCard, share: Share2, handshake: Handshake,
  // Lucide no trae el logo de WhatsApp (las marcas salieron de la librería):
  // se usa el globo de mensaje, que es lo más cercano sin usar la marca.
  whatsapp: MessageSquare,
} as const;

export type IconName = keyof typeof MAP;

interface Props extends Omit<LucideProps, "ref"> {
  name: IconName;
  size?: number;
}

// Un solo grosor de trazo en toda la interfaz: mezclarlos hace que los iconos
// parezcan de dos familias distintas en la misma pantalla.
export default function Icon({ name, size = 16, strokeWidth = 1.75, ...rest }: Props) {
  const Cmp = MAP[name];
  return <Cmp size={size} strokeWidth={strokeWidth} aria-hidden {...rest} />;
}

// Puntuación con relleno proporcional: redondear 4,6 a 5 estrellas llenas
// muestra un perfecto que no existe, justo al lado del número real.
export function Stars({ value, size = 14 }: { value: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  const fila = (fill: boolean) =>
    [1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={1.75}
        className={fill ? "text-[#E8A23C]" : "text-white/15"}
        fill={fill ? "currentColor" : "none"}
        aria-hidden
      />
    ));
  return (
    <span className="relative inline-flex items-center gap-0.5 align-middle"
      role="img" aria-label={`${value.toFixed(1)} de 5 estrellas`}>
      {fila(false)}
      <span className="absolute inset-0 flex items-center gap-0.5 overflow-hidden"
        style={{ width: `${pct}%` }} aria-hidden>
        {fila(true)}
      </span>
    </span>
  );
}
